// FROM: https://github.com/modusign/modusign-client/blob/7357f1784bc6a8191b532718d2dcdce864aeb896/app/src/components/Modal/SignersWithoutSignatureFieldConfirmModal/index.tsx

import {
  ArrowIcon,
  Box,
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
  TextLink,
} from '@modusign/design-system';
import { ReactNode } from 'react';

import SignerWithRequiredFieldCount from '@app/features/apply-react-signing/components/Modal/SignerWithoutSignatureField';
import useResponsiveValue from '@app/hooks/useResponsiveValue';

import SignerList from './SignerList';
import SignerListItem from './SignerListItem';

export interface SignerHasNotFieldsConfirmModalProps {
  isOpen: boolean;
  signerTotalCount: number;
  signersWithoutSignatureField: Array<SignerWithRequiredFieldCount>;
  onCancelClick: () => void;
  onConfirmClick: () => void;
}

function SignersWithoutSignatureFieldConfirmModal({
  isOpen,
  signerTotalCount,
  signersWithoutSignatureField,
  onCancelClick,
  onConfirmClick,
}: SignerHasNotFieldsConfirmModalProps) {
  const firstSignerHasInfo =
    signersWithoutSignatureField.length > 0 &&
    (!!signersWithoutSignatureField[0].role ||
      (!!signersWithoutSignatureField[0].name && !!signersWithoutSignatureField[0].contact));
  const isSignerListDisplay = signerTotalCount > 1 || firstSignerHasInfo;

  const maxWidth = useResponsiveValue(['xs', 'xs', 'md']);

  if (isSignerListDisplay) {
    return (
      <SignerHasNotFieldsConfirmModalBase
        isOpen={isOpen}
        maxWidth={maxWidth}
        onCancelClick={onCancelClick}
        onConfirmClick={onConfirmClick}
      >
        <Text as="p" fontWeight="medium" textAlign="center">
          사인/도장을 지정하지 않은 서명자가 있습니다.
          <br />
          <Text color="textEmphasisBase" fontWeight="medium">
            아래 서명자의 날인 없이 전자서명이 완료됩니다.
          </Text>
        </Text>
        <SignerList>
          {signersWithoutSignatureField.map(
            ({ contact, signingMethod, name, id, requiredFieldCount, role }, index) => (
              <SignerListItem
                contact={contact ?? ''}
                isLast={index === signersWithoutSignatureField.length - 1}
                key={id}
                name={name ?? ''}
                requiredFieldCount={requiredFieldCount}
                role={role}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                signingMethod={signingMethod!}
              />
            )
          )}
        </SignerList>
      </SignerHasNotFieldsConfirmModalBase>
    );
  }

  return (
    <SignerHasNotFieldsConfirmModalBase
      isOpen={isOpen}
      maxWidth="xs"
      onCancelClick={onCancelClick}
      onConfirmClick={onConfirmClick}
    >
      <Text as="p" fontWeight="medium" textAlign="center">
        사인/도장을 지정하지 않았습니다.
        <br />
        <Text color="textEmphasisBase" fontWeight="medium">
          서명자의 날인 없이 전자서명이 완료됩니다.
        </Text>
      </Text>
    </SignerHasNotFieldsConfirmModalBase>
  );
}

export default SignersWithoutSignatureFieldConfirmModal;

function SignerHasNotFieldsConfirmModalBase({
  children,
  isOpen,
  maxWidth,
  onCancelClick,
  onConfirmClick,
}: {
  children: ReactNode;
  isOpen: boolean;
  maxWidth: string;
  onCancelClick: () => void;
  onConfirmClick: () => void;
}) {
  return (
    <Modal isOpen={isOpen} maxWidth={maxWidth} onClose={onCancelClick}>
      <ModalTitle>입력 설정 확인</ModalTitle>
      <ModalContent textAlign="center">
        {children}
        <Box mt={6}>
          <TextLink
            href="https://support.modusign.co.kr/85687121463"
            rel="noopener noreferrer"
            target="_blank"
          >
            사인/도장이 없는 서명 요청 유의사항
          </TextLink>
        </Box>
      </ModalContent>
      <ModalFooter>
        <Button mr={3} variant="basic" onClick={onCancelClick}>
          닫기
        </Button>
        <Button variant="primary" onClick={onConfirmClick}>
          계속 진행
          <ArrowIcon direction="right" ml={1} />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
